import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { VbWithdrawalStepFormGroup } from '../../../../../models/shared/forms/vb-withdrawal-step-form-group';
import { MemberAccountFundAllocation } from '../../../../../models/transactions/member-account-fund-allocation';
import { TypeService } from '../../../../../services/type/type-service';
import { map, startWith } from 'rxjs/operators';
import { Subscribable } from '../../../../../models/base/subscribable';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-vb-withdrawal-instructions-card',
  templateUrl: './vb-withdrawal-instructions-card.component.html',
  styleUrls: ['./vb-withdrawal-instructions-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VbWithdrawalInstructionsCardComponent extends Subscribable implements OnChanges {
  @Input() formGroup!: VbWithdrawalStepFormGroup;
  @Input() formSubmitted: boolean = false;
  @Input() accountAllocations: MemberAccountFundAllocation[] = [];
  @Input() isLoading!: boolean;

  private typeService = inject(TypeService);
  public fundTypes$ = this.typeService.fundTypes$.pipe(map(types => types.filter(type => !type.isCash())));

  private _externalLoading = new BehaviorSubject<boolean>(false);
  public externalLoading$ = this._externalLoading as Observable<boolean>;

  public isLoading$ = combineLatest([this.typeService.fundTypesAreLoaded$, this.externalLoading$]).pipe(
    map(([externalLoading, typesAreLoaded]) => !externalLoading && typesAreLoaded)
  );

  public percentageTotal = '0.00';
  public errorText = '';

  private percentageSubscription?: Subscription;

  private listenToPercentageChanges(): void {
    this.percentageSubscription?.unsubscribe();

    this.percentageSubscription = this.formGroup.controls.instructions.valueChanges
      .pipe(startWith(this.formGroup.controls.instructions.getRawValue()))
      .subscribeWhileAlive({
        owner: this,
        next: () => {
          const rawValues = this.formGroup.controls.instructions.getRawValue();
          const percentages = rawValues.map(instruction => instruction.percentage);
          this.percentageTotal = this.calculatePercentageTotal(percentages);
          if (+this.percentageTotal < 100 && +this.percentageTotal !== 0) {
            this.errorText = $localize`You must allocate the remaining ${(100 - +this.percentageTotal).toFixed(
              2
            )}% of the withdrawal percentage`;
          } else if (+this.percentageTotal > 100) {
            this.errorText = $localize`The total withdrawal percentage is in excess of ${(
              +this.percentageTotal - 100
            )?.toFixed(2)}%`;
          } else if (+this.percentageTotal === 0) {
            this.errorText = $localize`You must set the withdrawal percentage`;
          }
        }
      });
  }

  private calculatePercentageTotal(percentages: (number | null | undefined)[]): string {
    const total: number = percentages.reduce<number>((sum, val) => {
      const percentage = val ?? 0;
      return sum + (percentage > 0 ? percentage : 0);
    }, 0);
    return total.toFixed(2);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.formGroup && !!this.formGroup) {
      this.listenToPercentageChanges();
    }
    if (changes.isLoading) {
      this._externalLoading.next(this.isLoading);
    }
  }
}
